import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const MIN_CAP = 1;
const MAX_CAP = 100;

const AIPersonaCap = ({ initialCap, defaultCap, onSaved }) => {
  const [value, setValue] = useState(String(initialCap ?? defaultCap ?? 15));
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (initialCap !== undefined && initialCap !== null) setValue(String(initialCap));
  }, [initialCap]);

  const dirty = String(initialCap ?? '') !== value;

  const handleSave = async () => {
    const num = Number(value);
    if (!Number.isInteger(num) || num < MIN_CAP || num > MAX_CAP) {
      toast({
        title: 'Invalid value',
        description: `Persona cap must be a whole number between ${MIN_CAP} and ${MAX_CAP}.`,
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/ai-discussion/persona-cap`,
        { quarterly_persona_cap: num },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: `Persona cap set to ${num}/book/quarter`, description: res.data?.message || 'Saved.' });
      if (onSaved) onSaved(num);
    } catch (err) {
      toast({
        title: 'Could not save persona cap',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10" data-testid="ai-persona-cap">
      <div className="flex items-center gap-2 mb-2">
        <Users className="h-4 w-4 text-cyan-400" />
        <span className="text-xs font-semibold text-cyan-300 uppercase tracking-wide">Persona Cap</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-300 whitespace-nowrap" htmlFor="ai-persona-cap-input">
          Max personas / book / quarter
        </label>
        <input
          id="ai-persona-cap-input"
          type="number"
          min={MIN_CAP}
          max={MAX_CAP}
          step={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-20 bg-zinc-900 border border-white/15 rounded-md px-2 py-1 text-white text-sm focus:border-cyan-500 focus:outline-none"
          data-testid="ai-persona-cap-input"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={saving || !dirty}
          onClick={handleSave}
          className="border-white/15 text-white hover:bg-white/10"
          data-testid="ai-persona-cap-save"
        >
          {saving ? '...' : <><Check className="h-3.5 w-3.5 mr-1" /> Save</>}
        </Button>
      </div>
      <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
        Once a book's discussion reaches this many distinct AI readers in a calendar quarter,
        new posts come only from those recurring readers (who keep following the book chapter by chapter).
        Default {defaultCap ?? 15}.
      </p>
    </div>
  );
};

export default AIPersonaCap;
